/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { setLastSelectedBranchId } from '../../shared/localPrefs';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { usePublicStoreFeaturesVisible } from '../../shell/feature-flags/usePublicStoreFeaturesVisible';
import { useNavVisibilityContext } from '../../shell/navigation/useNavVisibilityContext';
import { useOwnerPanelAccess } from '../hooks/useOwnerPanelAccess';
import { useScreenRefresh } from '../../shared/ui/hooks-screen/useScreenRefresh';
import { loadOwnerPanelData } from '../application/loadOwnerPanelData';
import type { OwnerPanelPeriod, OwnerPanelSummary } from '../domain/types';
import { navigateInShell, navigateRoot } from '../../shell/navigation/rootNavigation';

export type OwnerPanelEntryTarget = 'shell' | 'post_login_hub';

export function useOwnerBranchesPanelScreen(
  navigation: NavigationProp<ParamListBase>,
  options?: { entryTarget?: OwnerPanelEntryTarget },
) {
  const entryTarget = options?.entryTarget ?? 'shell';
  const allBranches = useSessionStore((s) => s.branches);
  const branchesLoading = useSessionStore((s) => s.branchesLoading);
  const branches = useMemo(
    () => allBranches.filter((b) => b.isActive !== false),
    [allBranches],
  );
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const setCurrentBranch = useSessionStore((s) => s.setCurrentBranch);
  const { entitlements, loading: entitlementsLoading } = usePlanEntitlements();
  const { canAccessOwnerPanel, isOwner } = useOwnerPanelAccess();
  const accessResolved = !entitlementsLoading && !branchesLoading;
  const navCtx = useNavVisibilityContext();
  const hasInventory = useHasBusinessCapability('inventory');
  const hasAppointments = useHasBusinessCapability('appointments');
  const publicStoreOn = usePublicStoreFeaturesVisible();
  // Hub post-login no tiene sucursal activa → allowedModules vacío. El panel del dueño
  // siempre muestra estado de caja (firma del proto), sin depender del nav de un local.
  const cashAllowed =
    entitlements?.is_owner === true || navCtx.allowedModules.has('Cash');

  const [period, setPeriod] = useState<OwnerPanelPeriod>('today');
  const [summary, setSummary] = useState<OwnerPanelSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  const organizationName = entitlements?.organization_name?.trim() || 'Tu negocio';

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      if (!accessResolved) return;
      if (!canAccessOwnerPanel) {
        setSummary(null);
        return;
      }
      const orgId = await getSessionOrganizationId();
      if (!orgId || branches.length === 0) {
        setSummary(null);
        return;
      }
      const data = await loadOwnerPanelData({
        orgId,
        branches,
        period,
        isOwner,
        hasInventory,
        hasAppointments,
        cashAllowed,
        publicStoreOn,
      });
      setSummary(data);
      setLastRefreshedAt(new Date());
    } catch {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [accessResolved, branches, canAccessOwnerPanel, period, hasInventory, hasAppointments, cashAllowed, publicStoreOn, isOwner]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const { refreshing, onRefresh } = useScreenRefresh(reload);

  const goToBranch = useCallback(
    async (branchId: string) => {
      if (entryTarget === 'post_login_hub') {
        setCurrentBranch(branchId);
        await setLastSelectedBranchId(branchId);
        navigateRoot(navigation, 'AppShell');
        return;
      }
      if (branchId === currentBranchId) {
        navigateInShell(navigation, 'MainTabs', { screen: 'Home' });
        return;
      }
      setCurrentBranch(branchId);
      await setLastSelectedBranchId(branchId);
      navigateInShell(navigation, 'MainTabs', { screen: 'Home' });
    },
    [currentBranchId, entryTarget, navigation, setCurrentBranch],
  );

  const openReports = useCallback(async () => {
    if (entryTarget === 'post_login_hub') {
      const targetBranchId =
        currentBranchId ?? branches.find((b) => b.isActive !== false)?.id ?? null;
      if (targetBranchId && targetBranchId !== currentBranchId) {
        setCurrentBranch(targetBranchId);
        await setLastSelectedBranchId(targetBranchId);
      }
      navigateRoot(navigation, 'AppShell', {
        screen: 'MainTabs',
        params: { screen: 'Reports' },
      });
      return;
    }
    navigateInShell(navigation, 'MainTabs', { screen: 'Reports' });
  }, [branches, currentBranchId, entryTarget, navigation, setCurrentBranch]);

  return {
    branches,
    organizationName,
    canAccessOwnerPanel,
    accessResolved,
    period,
    setPeriod,
    summary,
    loading,
    refreshing,
    onRefresh,
    goToBranch,
    openReports,
    currentBranchId,
    hasInventory,
    hasAppointments,
    publicStoreOn,
    lastRefreshedAt,
  };
}
